import { takeEvery, put } from "redux-saga/effects";
import api from "../../../utils/apiService";
import { ENDPOINT } from "../../../config/endpoints";
import { notifySuccess } from "../../../effects-bridge";
import { BRANCH_OPTIONS } from "../../../utils/constants";
import {
  getAllOutlets,
  getAllOutletsSuccess,
  getAllActivitiesPerOutlet,
  toggleOutlet,
  toggleOutletSuccess,
  fetchOutletStatus,
  fetchActivityError,
  bulkOutletToggle,
  bulkOutletToggleSuccess,
  updateOutletDeliveryStatus,
  getOutletDeliveryStatus,
  getHelpLink,
  toggleBranch,
  getBranchData,
  allOutletsCallComplete,
} from "../../slices/OutletStatusSlice";
import {
  fetchActivities,
  fetchActivityErrorFun,
  fetchHelpLink,
  fetchOutletStatusFun,
  getBranchDataFunc,
  getOutletDeliveryStatusFun,
  updateOutletDeliveryStatusFun,
} from "./shared";

export * from "./shared";

/**
 * WEB half of `outletStatus`. `shared.ts` owns the seven identical workers and documents every
 * divergence; this file carries the three that differ, the one web-only worker, and `root`.
 *
 * ⚠️ Every core import names the VARIANT, not the folder (hazard 6/16).
 *
 * `ToastNotifySuccess` became `notifySuccess` from the effects bridge — UI cannot come into
 * core. Mobile has this call commented out in its app source, so `index.native.ts` deliberately
 * has no toast at all.
 */
function* getAllOutletsFun(): Generator<any, void, any> {
  try {
    const response = yield api.request({
      method: "POST",
      url: ENDPOINT.OUTLETSTATUS.getAllOutlets(),
      data: { aggregatorType: "Order", branches: "current", isNew: true },
    });
    yield put(getAllOutletsSuccess(response));
  } catch (error) {
    console.log("error", error);
  } finally {
    // WEB ONLY — mobile has no finally and never dispatches this.
    yield put(allOutletsCallComplete());
  }
}

function* toggleOutletFun(action: any): Generator<any, void, any> {
  try {
    let response = yield api.request({
      method: "POST",
      url: ENDPOINT.OUTLETSTATUS.toggleOutletStatus(),
      data: action?.payload,
    });
    if (!Object.keys(response)?.length) {
      // WEB ONLY — mobile's app source has this line commented out.
      notifySuccess("Status Updated Successfully");
      response = {
        status: "success",
      };
    }
    yield put(toggleOutletSuccess(response));
  } catch (error) {
    console.log("error", error);
  }
}

function* bulkOutletToggleFun(action: any): Generator<any, void, any> {
  try {
    let response = yield api.request({
      method: "POST",
      url: ENDPOINT.OUTLETSTATUS.bulkOutletToggle(),
      data: action?.payload,
    });
    // Diverges from mobile, which guards on `!response` rather than `!response?.code`.
    if (!response?.code) {
      response = {
        status: "success",
      };
    }
    yield put(bulkOutletToggleSuccess(response));
  } catch (error) {
    console.log("error", error);
  }
}

/** WEB ONLY — mobile has no branch toggle. */
function* toggleBranchFunc(action: any): Generator<any, void, any> {
  let { data, callback } = action.payload;
  try {
    const response = yield api.request({
      method: "POST",
      url: ENDPOINT.OUTLETSTATUS.toggleBranch(data?.branchID, data?.action),
      data: {
        outletClose: data?.action === BRANCH_OPTIONS.close ? true : false,
      },
    });
    if (response) {
      callback(response);
    }
  } catch (error) {
    console.log("error", error);
  }
}

export default function* root() {
  yield takeEvery(getAllOutlets.type, getAllOutletsFun);
  yield takeEvery(getAllActivitiesPerOutlet.type, fetchActivities);
  yield takeEvery(toggleOutlet.type, toggleOutletFun);
  yield takeEvery(fetchOutletStatus.type, fetchOutletStatusFun);
  yield takeEvery(fetchActivityError.type, fetchActivityErrorFun);
  yield takeEvery(bulkOutletToggle.type, bulkOutletToggleFun);
  yield takeEvery(getOutletDeliveryStatus.type, getOutletDeliveryStatusFun);
  yield takeEvery(updateOutletDeliveryStatus.type, updateOutletDeliveryStatusFun);
  yield takeEvery(getHelpLink.type, fetchHelpLink);
  yield takeEvery(toggleBranch.type, toggleBranchFunc);
  yield takeEvery(getBranchData.type, getBranchDataFunc);
}
